/**
 * Chiamate dei tag (F-079). Nessun URL scritto nei componenti: stanno qui.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { instrumentKeys } from '@/features/instruments/api';
import { wishlistKeys } from '@/features/wishlist/api';

export const tagKeys = {
  all: ['tags'] as const,
  list: ['tags', 'all'] as const,
  view: (tag: string) => ['tags', 'view', tag] as const,
};

export type TagCount = { tag: string; count: number };

export type TaggedInstrument = {
  instrumentId: string;
  name: string;
  symbol: string | null;
  isin: string | null;
  currency: string;
  marketMic: string | null;
  tags: string[];
};

/** Tutti i tag dell'utente, dal più usato: alimenta i suggerimenti del modal. */
export function useAllTags() {
  return useQuery({
    queryKey: tagKeys.list,
    queryFn: () => api.get<{ items: TagCount[] }>('/tags'),
  });
}

export function useTagView(tag: string) {
  return useQuery({
    queryKey: tagKeys.view(tag),
    queryFn: () => api.get<{ tag: string; items: TaggedInstrument[]; total: number }>(`/tags/${encodeURIComponent(tag)}`),
    enabled: tag.length > 0,
  });
}

/**
 * Salva l'insieme completo dei tag di un titolo.
 *
 * Invalida **tutto** ciò che porta tag e non solo il titolo toccato: l'elenco
 * dei tag cambia se questo era l'ultimo uso di uno di essi, la wishlist li
 * mostra in piccolo, e la vista per tag può aver perso o guadagnato una riga.
 * Invalidare il solo dettaglio lascerebbe le altre schermate a mostrare una
 * verità vecchia finché non si ricarica la pagina.
 */
export function useSaveTags(instrumentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tags: string[]) => api.put<{ tags: string[] }>(`/instruments/${instrumentId}/tags`, { tags }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tagKeys.all });
      void queryClient.invalidateQueries({ queryKey: instrumentKeys.detail(instrumentId) });
      void queryClient.invalidateQueries({ queryKey: wishlistKeys.all });
    },
  });
}
